import { inject, Injectable, Injector } from '@angular/core';
import { ConnectionStatus } from '@capacitor/network';
import { defer, map, Observable } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { NetworkStatusService } from './network-status.service';

/**
 * NgRx-free drop-in replacement for `@curbnturf/status`'s StatusFacade —
 * injected directly by name (`inject(LocalStatusFacade)`, e.g. in
 * unauthenticated-error.interceptor.ts) rather than via a DI-token override,
 * since nothing in cnt-landing's graph still constructs the real
 * `@curbnturf/status` class. Same public shape, sourced from AuthService's
 * Cognito-backed session instead of the NgRx `auth`/`network` buckets.
 *
 * AuthService is resolved lazily via Injector.get() (not constructor
 * injection) deliberately: the shared UserService's own constructor injects
 * StatusFacade, and AuthService injects UserService directly, so a
 * constructor-time dependency on AuthService here would close a real
 * circular loop (AuthService -> UserService -> StatusFacade ->
 * LocalStatusFacade -> AuthService) and throw NG0200 at bootstrap. Deferring
 * the lookup to first subscription (well after all three are constructed)
 * avoids it — AuthService is `providedIn: 'root'` and already resolved by
 * then in every real path (the HTTP interceptor is the only actual
 * subscriber, at request time).
 */
@Injectable({ providedIn: 'root' })
export class LocalStatusFacade {
  private readonly injector = inject(Injector);
  private readonly networkStatus = inject(NetworkStatusService);

  private get auth(): AuthService {
    return this.injector.get(AuthService);
  }

  readonly loggedIn$: Observable<boolean> = defer(() => this.auth.currentUser$).pipe(map((user) => !!user));
  readonly userId$: Observable<number | undefined> = defer(() => this.auth.currentUser$).pipe(map((user) => user?.id));
  readonly networkStatus$: Observable<ConnectionStatus> = this.networkStatus.status$;
}
